import type { ThemeColorKey, ThemeSettings } from '../../types/theme';
import { contrastColor, mix, rgbTriplet, shade, tint } from './color';
import { findThemeFont } from './themeFonts';

/** Barvy, které Bootstrap používá pro tlačítka/alerty/badge (mají odvozené odstíny). */
export const BRAND_KEYS = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'accent'] as const;

const kebab = (key: string): string => key.replace(/[A-Z]/g, c => '-' + c.toLowerCase());

/** Tmavé varianty neutrálních barev, když je téma nezadá. */
const DARK_NEUTRALS: Partial<Record<ThemeColorKey, string>> = {
	bg: '#16181d',
	surface: '#1e2128',
	text: '#dee2e6',
	textMuted: '#9aa3ad',
	border: '#3a3f47',
};

/** O kolik se barva zesvětlí v tmavém režimu, aby měla na tmavém pozadí dost kontrastu. */
const DARK_LIGHTEN: Partial<Record<ThemeColorKey, number>> = {
	primary: 0.3, secondary: 0.25, accent: 0.1, success: 0.2, danger: 0.2,
};

/** Paleta pro tmavý režim: explicitní hodnoty z tématu, jinak odvozené ze světlé palety. */
export const resolveDarkColors = (settings: ThemeSettings): Record<ThemeColorKey, string> => {
	const out = {} as Record<ThemeColorKey, string>;
	for (const key of Object.keys(settings.colors.light) as ThemeColorKey[]) {
		const explicit = settings.colors.dark[key];
		out[key] = explicit ?? DARK_NEUTRALS[key] ?? tint(settings.colors.light[key], DARK_LIGHTEN[key] ?? 0);
	}
	return out;
};

const colorTokens = (colors: Record<ThemeColorKey, string>): string[] => {
	const lines: string[] = [];
	for (const key of Object.keys(colors) as ThemeColorKey[]) {
		const name = kebab(key);
		lines.push(`--fx-${name}: ${colors[key]};`, `--fx-${name}-rgb: ${rgbTriplet(colors[key])};`);
	}
	for (const name of BRAND_KEYS) {
		const c = colors[name];
		const on = contrastColor(c);
		// stejně jako Bootstrapův button-variant(): u světlého textu se tmaví, u tmavého zesvětluje
		const adjust = on === '#ffffff' ? shade : tint;
		lines.push(
			`--fx-on-${name}: ${on};`,
			`--fx-${name}-hover-bg: ${adjust(c, 0.15)};`,
			`--fx-${name}-hover-border: ${adjust(c, 0.2)};`,
			`--fx-${name}-active-bg: ${adjust(c, 0.2)};`,
			`--fx-${name}-active-border: ${adjust(c, 0.25)};`,
			`--fx-${name}-focus-rgb: ${rgbTriplet(mix(contrastColor(c), c, 0.15))};`,
		);
	}
	return lines;
};

export type ThemeLogos = { light: string | null; dark: string | null };

/** Osobní přepisy tématu (docs/tasks/063-vzhled-2.md); null = z tématu. */
export type ThemeOverrides = { density?: number | null; fontScale?: number | null };

/**
 * Z nastavení tématu vyrobí CSS s tokeny `--fx-*` (viz assets/styles/_tokens.scss a
 * _theme-runtime.scss, které je mapují na Bootstrap). Vkládá se do `<style id="fx-theme">`.
 */
export const buildThemeCss = (
	settings: ThemeSettings,
	logos: ThemeLogos = { light: null, dark: null },
	overrides: ThemeOverrides = {},
): string => {
	const density = Math.round(overrides.density ?? settings.density) / 100;
	const fontScale = Math.round(overrides.fontScale ?? 100) / 100;
	const light = [
		...colorTokens(settings.colors.light),
		`--fx-font-body: ${findThemeFont(settings.fonts.body).css};`,
		`--fx-font-heading: ${findThemeFont(settings.fonts.heading).css};`,
		`--fx-density: ${density};`,
		`--fx-font-scale: ${fontScale};`,
		`--fx-logo-light: ${logos.light ? `url("${logos.light}")` : 'none'};`,
		`--fx-logo-dark: ${logos.dark ? `url("${logos.dark}")` : 'none'};`,
	];
	const dark = colorTokens(resolveDarkColors(settings));

	return `:root, [data-bs-theme="light"] {\n  ${light.join('\n  ')}\n}\n[data-bs-theme="dark"] {\n  ${dark.join('\n  ')}\n}\n`;
};
